import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GitBranch, Plus, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import TreeNode, { CentroCostoNode } from '../components/TreeNode';
import CdCSelector from '../components/CdCSelector';

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

const TIPO_OPTIONS = ['produttivo', 'ausiliario', 'funzionale', 'comune'];
const NATURA_OPTIONS = ['costo', 'ricavo', 'profitto'];

interface NuovoCentroForm {
  codice: string;
  nome: string;
  tipo: string;
  natura: string;
  parent_id: string | null;
}

const emptyForm = (): NuovoCentroForm => ({
  codice: '',
  nome: '',
  tipo: 'produttivo',
  natura: 'costo',
  parent_id: null,
});

const CentriCostoTree: React.FC = () => {
  const navigate = useNavigate();
  const [tree, setTree] = useState<CentroCostoNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<NuovoCentroForm>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const caricaAlbero = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/centri-costo/centri/albero', {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Errore caricamento albero: ${res.status}`);
      const data: CentroCostoNode[] = await res.json();
      setTree(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    caricaAlbero();
  }, [caricaAlbero]);

  const handleSave = useCallback(async () => {
    if (!form.codice.trim() || !form.nome.trim()) {
      setSaveError('Codice e nome sono obbligatori');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch('/api/v1/centri-costo/centri', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail ?? `Errore creazione: ${res.status}`);
      }
      setForm(emptyForm());
      setShowForm(false);
      await caricaAlbero();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setSaving(false);
    }
  }, [form, caricaAlbero]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GitBranch className="h-5 w-5 text-gray-400" />
          <h1 className="text-2xl font-semibold">Centri di Costo</h1>
        </div>
        <button
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          onClick={() => setShowForm((v) => !v)}
        >
          {showForm ? <ChevronUp className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          Nuovo centro
        </button>
      </div>

      {/* Inline creation form */}
      {showForm && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
          <h2 className="text-sm font-semibold text-gray-700">Nuovo centro di costo</h2>
          {saveError && (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
              {saveError}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Codice *</label>
              <input
                type="text"
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={form.codice}
                onChange={(e) => setForm((f) => ({ ...f, codice: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nome *</label>
              <input
                type="text"
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={form.nome}
                onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tipo</label>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={form.tipo}
                onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value }))}
              >
                {TIPO_OPTIONS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Natura</label>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={form.natura}
                onChange={(e) => setForm((f) => ({ ...f, natura: e.target.value }))}
              >
                {NATURA_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Centro padre</label>
              <CdCSelector
                value={form.parent_id}
                onChange={(id) => setForm((f) => ({ ...f, parent_id: id }))}
                placeholder="Radice (nessun padre)"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-100"
              onClick={() => { setShowForm(false); setForm(emptyForm()); setSaveError(null); }}
            >
              Annulla
            </button>
            <button
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Salvataggio…' : 'Crea centro'}
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Tree */}
      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Caricamento…
        </div>
      ) : tree.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400 gap-3">
          <GitBranch className="h-10 w-10 opacity-30" />
          <p className="text-sm">Nessun centro di costo. Crea il primo centro.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
          {tree.map((node) => (
            <TreeNode
              key={node.id}
              node={node}
              level={0}
              onSelect={(id) => navigate(`/centri-costo/centri/${id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CentriCostoTree;
